import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Card,
  CircularProgress,
  LinearProgress,
  Icon,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Modal,
  Box,
  Divider,
  Chip,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, stagger } from "../../utils/animations";

const API_TEXTES = "/api/textes/";
const API_DOMAINES = "/api/domaines/";
const API_TYPES = "/api/types-textes/";

function BaseDocumentaire() {
  const [textes, setTextes] = useState([]);
  const [domaines, setDomaines] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchFilter, setSearchFilter] = useState("");
  const [domaineFilter, setDomaineFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // ── Progression téléchargement ──
  const [downloadProgress, setDownloadProgress] = useState({
    open: false,
    progress: 0,
    filename: "",
  });

  const handleDownload = useCallback(async (doc) => {
    setDownloadProgress({ open: true, progress: 0, filename: doc.nom_fichier });
    try {
      const res = await api.get(`/api/textes/document/download/${doc.id}`, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress((prev) => ({ ...prev, progress: pct }));
          }
        },
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = doc.nom_fichier;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // silently ignore
    } finally {
      setDownloadProgress({ open: false, progress: 0, filename: "" });
    }
  }, []);

  // ── État modal exigences ──
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTexte, setSelectedTexte] = useState(null);
  const [exigences, setExigences] = useState([]);
  const [loadingExigences, setLoadingExigences] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [resTextes, resDomaines, resTypes] = await Promise.all([
        api.get(`${API_TEXTES}consultation`),
        api.get(API_DOMAINES),
        api.get(API_TYPES),
      ]);
      setTextes(resTextes.data);
      setDomaines(resDomaines.data);
      setTypes(resTypes.data);
    } catch {
      // Silently handle
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openExigences = useCallback(async (texte) => {
    setSelectedTexte(texte);
    setModalOpen(true);
    setExigences([]);
    setLoadingExigences(true);
    try {
      const res = await api.get(`/api/exigences/texte/${texte.id}`);
      setExigences(res.data);
    } catch {
      setExigences([]);
    } finally {
      setLoadingExigences(false);
    }
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedTexte(null);
    setExigences([]);
  }, []);

  const filteredTextes = useMemo(() => {
    return textes
      .filter((t) => {
        const term = searchFilter.toLowerCase();
        const matchesSearch =
          !searchFilter.trim() ||
          t.titre?.toLowerCase().includes(term) ||
          t.reference?.toLowerCase().includes(term);
        const matchesDomaine = !domaineFilter || t.domaine?.id === domaineFilter;
        const matchesType = !typeFilter || t.type_texte?.id === typeFilter;
        return matchesSearch && matchesDomaine && matchesType;
      })
      .sort((a, b) => b.id - a.id);
  }, [textes, searchFilter, domaineFilter, typeFilter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* En-tête */}
        <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="purple"
            borderRadius="lg"
            coloredShadow="dark"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
          >
            <MDBox>
              <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                Textes réglementaires
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
              >
                Consultation des textes réglementaires applicables à votre institution
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Barre de filtres */}
          <MDBox px={2} pt={2} pb={2}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  placeholder="Rechercher par titre ou référence..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  textTransform="uppercase"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  Domaine
                </MDTypography>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={domaineFilter}
                    onChange={(e) => setDomaineFilter(e.target.value)}
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">Tous les domaines</MenuItem>
                    {domaines.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  textTransform="uppercase"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  Type d&apos;acte
                </MDTypography>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">Tous les types</MenuItem>
                    {types.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <MDButton
                  variant="gradient"
                  color="purple"
                  fullWidth
                  sx={{ height: "45px" }}
                  onClick={() => {
                    setSearchFilter("");
                    setDomaineFilter("");
                    setTypeFilter("");
                  }}
                >
                  <Icon sx={{ mr: 0.5 }}>restart_alt</Icon>
                  Reset
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

        {/* Contenu - Cards */}
        <MDBox mt={3}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" p={5}>
              <CircularProgress color="info" />
            </MDBox>
          ) : filteredTextes.length > 0 ? (
            <Grid container spacing={3}>
              {filteredTextes.map((texte, idx) => {
                const docs = (texte.versions || []).flatMap((v) =>
                  (v.documents || []).map((d) => ({ ...d, version: v.version }))
                );
                return (
                  <Grid item xs={12} md={6} lg={4} key={texte.id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "14px",
                        overflow: "hidden",
                        border: "1px solid rgba(0,0,0,0.07)",
                        animation: `${fadeInUp} 0.4s ease-out ${stagger(idx, 0.06)} both`,
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 32px rgba(10,52,117,0.12)",
                        },
                      }}
                    >
                      {/* ── Bande supérieure ── */}
                      <MDBox
                        sx={{
                          background: "linear-gradient(135deg, #0A3475, #186A81)",
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Chip
                          label={texte.type_texte?.type || "Type inconnu"}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.18)",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "0.62rem",
                            height: 22,
                            maxWidth: "65%",
                            "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                          }}
                        />
                        <MDBox display="flex" alignItems="center" gap={0.8}>
                          {texte.est_analyse && (
                            <Chip
                              icon={
                                <Icon
                                  sx={{ fontSize: "12px !important", color: "#0A3475 !important" }}
                                >
                                  verified
                                </Icon>
                              }
                              label="Analysé"
                              size="small"
                              sx={{
                                backgroundColor: "#FFD740",
                                color: "#0A3475",
                                fontWeight: "bold",
                                fontSize: "0.6rem",
                                height: 20,
                              }}
                            />
                          )}
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: "rgba(255,255,255,0.7)",
                              fontSize: "0.65rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {texte.date_publication || "-"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      {/* ── Corps ── */}
                      <MDBox px={2.5} pt={2} pb={1} flex={1} display="flex" flexDirection="column">
                        {/* Titre */}
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          sx={{
                            fontSize: "0.95rem",
                            lineHeight: 1.4,
                            mb: 0.8,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {texte.titre}
                        </MDTypography>

                        {/* Référence */}
                        {texte.reference && (
                          <MDTypography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "#186A81",
                              fontWeight: "medium",
                              mb: 1.5,
                              fontSize: "0.72rem",
                            }}
                          >
                            {texte.reference}
                          </MDTypography>
                        )}

                        {/* Domaine */}
                        <MDBox display="flex" alignItems="center" mt="auto" mb={2}>
                          <MDBox
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              backgroundColor: "#f0f4fa",
                              borderRadius: "20px",
                              px: 1.2,
                              py: 0.4,
                              gap: 0.5,
                            }}
                          >
                            <Icon sx={{ color: "#0A3475", fontSize: "14px !important" }}>
                              domain
                            </Icon>
                            <MDTypography
                              variant="caption"
                              sx={{ color: "#0A3475", fontWeight: "medium", fontSize: "0.7rem" }}
                            >
                              {texte.domaine?.nom || "Domaine inconnu"}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>

                      {/* ── Pied de carte ── */}
                      <MDBox
                        px={2}
                        py={1.5}
                        sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", backgroundColor: "#fafbfc" }}
                      >
                        {/* Bouton Exigences */}
                        <MDButton
                          variant="gradient"
                          color="info"
                          fullWidth
                          size="small"
                          onClick={() => openExigences(texte)}
                          sx={{
                            mb: docs.length > 0 ? 1 : 0,
                            fontSize: "0.75rem",
                            py: 0.9,
                            borderRadius: "8px",
                            textTransform: "none",
                          }}
                        >
                          <Icon sx={{ mr: 0.8, fontSize: "16px !important" }}>fact_check</Icon>
                          Exigences
                          {texte.est_analyse && (
                            <MDBox
                              component="span"
                              sx={{
                                ml: 0.8,
                                backgroundColor: "rgba(255,255,255,0.3)",
                                borderRadius: "10px",
                                px: 0.7,
                                fontSize: "0.62rem",
                                fontWeight: "bold",
                              }}
                            >
                              ✓
                            </MDBox>
                          )}
                        </MDButton>

                        {/* Documents */}
                        {docs.length > 0 && (
                          <MDBox display="flex" flexDirection="column" gap={0.6}>
                            {docs.map((doc, docIdx) => (
                              <MDBox
                                key={`${doc.id}-${docIdx}`}
                                display="flex"
                                alignItems="center"
                                sx={{
                                  borderRadius: "8px",
                                  border: "1px solid rgba(24,106,129,0.25)",
                                  px: 1.5,
                                  py: 0.7,
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  "&:hover": { backgroundColor: "#e4f2f5", borderColor: "#186A81" },
                                }}
                                onClick={() => handleDownload(doc)}
                                title={doc.nom_fichier}
                              >
                                <Icon
                                  sx={{
                                    color: "#186A81",
                                    fontSize: "16px !important",
                                    mr: 1,
                                    flexShrink: 0,
                                  }}
                                >
                                  picture_as_pdf
                                </Icon>
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "#186A81",
                                    fontWeight: "medium",
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {doc.nom_fichier.length > 32
                                    ? doc.nom_fichier.substring(0, 32) + "…"
                                    : doc.nom_fichier}
                                </MDTypography>
                                <Icon
                                  sx={{
                                    color: "#186A81",
                                    fontSize: "14px !important",
                                    ml: 0.5,
                                    flexShrink: 0,
                                  }}
                                >
                                  download
                                </Icon>
                              </MDBox>
                            ))}
                          </MDBox>
                        )}

                        {docs.length === 0 && (
                          <MDBox display="flex" alignItems="center" sx={{ opacity: 0.4 }}>
                            <Icon sx={{ fontSize: "14px !important", mr: 0.5 }}>info_outline</Icon>
                            <MDTypography variant="caption" sx={{ fontSize: "0.68rem" }}>
                              Aucun document disponible
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <MDBox textAlign="center" py={5}>
              <MDTypography variant="h6" color="text">
                Aucun résultat trouvé.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      {/* ═══════ MODAL EXIGENCES ═══════ */}
      <Modal open={modalOpen} onClose={closeModal}>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: "80vw", md: "680px" },
            maxHeight: "85vh",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            {/* En-tête modal */}
            <MDBox
              sx={{
                background: "linear-gradient(135deg, #0A3475, #186A81)",
                px: 3,
                py: 2.5,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <MDBox flex={1} pr={2}>
                <MDBox display="flex" alignItems="center" mb={0.5}>
                  <Icon sx={{ color: "#FFD740", mr: 1, fontSize: "20px !important" }}>
                    fact_check
                  </Icon>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    Exigences réglementaires
                  </MDTypography>
                </MDBox>
                <MDTypography
                  variant="caption"
                  color="white"
                  sx={{ opacity: 0.8, lineHeight: 1.4, display: "block" }}
                >
                  {selectedTexte?.titre}
                </MDTypography>
              </MDBox>
              <MDBox
                onClick={closeModal}
                sx={{
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": { color: "#fff" },
                  flexShrink: 0,
                  mt: 0.3,
                }}
              >
                <Icon>close</Icon>
              </MDBox>
            </MDBox>

            {/* Corps scrollable */}
            <MDBox sx={{ overflowY: "auto", flex: 1, p: 0 }}>
              {loadingExigences ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                  <CircularProgress sx={{ color: "#186A81" }} />
                </MDBox>
              ) : exigences.length === 0 ? (
                <MDBox textAlign="center" py={6} px={3}>
                  <Icon sx={{ fontSize: "48px !important", color: "#ccc", mb: 1 }}>
                    assignment_late
                  </Icon>
                  <MDTypography variant="h6" color="text" sx={{ opacity: 0.5 }}>
                    Aucune exigence référencée
                  </MDTypography>
                  <MDTypography variant="body2" color="text" sx={{ opacity: 0.4, mt: 0.5 }}>
                    Ce texte n&apos;a pas encore été analysé.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox>
                  {/* Résumé */}
                  {selectedTexte?.resume && (
                    <MDBox
                      px={3}
                      py={2}
                      sx={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #eee" }}
                    >
                      <MDBox display="flex" alignItems="center" mb={0.8}>
                        <Icon sx={{ color: "#186A81", fontSize: "16px !important", mr: 0.8 }}>
                          subject
                        </Icon>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="text"
                          sx={{ textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.6 }}
                        >
                          Résumé
                        </MDTypography>
                      </MDBox>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{ lineHeight: 1.7, opacity: 0.8, fontSize: "0.82rem" }}
                      >
                        {selectedTexte.resume}
                      </MDTypography>
                    </MDBox>
                  )}

                  {/* Compteur */}
                  <MDBox
                    px={3}
                    py={1.5}
                    sx={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #eee" }}
                  >
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                      {exigences.length} exigence{exigences.length > 1 ? "s" : ""} identifiée
                      {exigences.length > 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>

                  {/* Liste */}
                  {exigences.map((ex, i) => (
                    <MDBox key={ex.id}>
                      <MDBox px={3} py={2}>
                        {/* Numéro + badges */}
                        <MDBox display="flex" alignItems="center" mb={1} gap={1}>
                          <MDBox
                            sx={{
                              minWidth: 26,
                              height: 26,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #0A3475, #186A81)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              color="white"
                              fontWeight="bold"
                              sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                            >
                              {i + 1}
                            </MDTypography>
                          </MDBox>

                          {ex.reference && (
                            <Chip
                              icon={
                                <Icon
                                  sx={{ fontSize: "12px !important", color: "#0A3475 !important" }}
                                >
                                  bookmark
                                </Icon>
                              }
                              label={ex.reference}
                              size="small"
                              sx={{
                                backgroundColor: "#e8eef8",
                                color: "#0A3475",
                                fontSize: "0.65rem",
                                fontWeight: "bold",
                                height: 20,
                              }}
                            />
                          )}
                          {ex.page && (
                            <Chip
                              icon={
                                <Icon
                                  sx={{ fontSize: "12px !important", color: "#186A81 !important" }}
                                >
                                  menu_book
                                </Icon>
                              }
                              label={`p. ${ex.page}`}
                              size="small"
                              sx={{
                                backgroundColor: "#e4f2f5",
                                color: "#186A81",
                                fontSize: "0.65rem",
                                fontWeight: "bold",
                                height: 20,
                              }}
                            />
                          )}
                        </MDBox>

                        {/* Texte de l'exigence */}
                        <MDTypography
                          variant="body2"
                          color="text"
                          sx={{ lineHeight: 1.7, pl: 4.5, fontSize: "0.82rem" }}
                        >
                          {ex.exigence}
                        </MDTypography>
                      </MDBox>
                      {i < exigences.length - 1 && <Divider sx={{ mx: 3, my: 0, opacity: 0.15 }} />}
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>

            {/* Pied de modal */}
            <MDBox
              px={3}
              py={2}
              sx={{
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={closeModal}
                sx={{ px: 3 }}
              >
                Fermer
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
      </Modal>

      {/* Modal progression téléchargement */}
      <Modal open={downloadProgress.open} onClose={() => {}}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: 420 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Icon sx={{ fontSize: "48px !important", color: "#186A81", mb: 1 }}>download</Icon>
          <MDTypography variant="h6" mb={0.5}>
            Téléchargement en cours...
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            sx={{ display: "block", mb: 2, opacity: 0.65 }}
          >
            {downloadProgress.filename}
          </MDTypography>
          <LinearProgress
            variant={downloadProgress.progress > 0 ? "determinate" : "indeterminate"}
            value={downloadProgress.progress}
            sx={{ borderRadius: 2, height: 8, mb: 1 }}
            color="info"
          />
          {downloadProgress.progress > 0 && (
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
              {downloadProgress.progress}%
            </MDTypography>
          )}
        </Box>
      </Modal>

      <Footer />
    </DashboardLayout>
  );
}

export default BaseDocumentaire;
